import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { MalayalamLayout } from "../components/malayalam/MalayalamLayout";
import { useMalayalamLang } from "../context/MalayalamLangContext";
import { C, IMG } from "../components/malayalam/mlShared";
import { mlPublicList, fileUrl } from "../lib/api";

// Section configuration. Placeholder items use the edition imagery until the
// migrated archive / new CMS content is imported.
const SECTIONS = {
  "movies": {
    title: "Malayalam Movies", accent: C.rust,
    subtitle: "Now playing, upcoming releases and the complete film archive.",
    items: [
      { title: "Now Showing", img: IMG.nowShowing },
      { title: "Upcoming", img: IMG.kerala1 },
      { title: "Recent Releases", img: IMG.kerala2 },
      { title: "OTT Releases", img: IMG.kerala3 },
      { title: "Film Archive", img: IMG.archive },
      { title: "Songs & Music", img: IMG.star },
    ],
  },
  "meet-the-star": {
    title: "Meet the Star", accent: C.gold,
    subtitle: "Profiles of actors, directors, musicians and the many artists of Mollywood.",
    items: [
      { title: "Actors", img: IMG.star }, { title: "Directors", img: IMG.intv2 },
      { title: "Music Directors", img: IMG.intv1 }, { title: "Singers", img: IMG.news },
      { title: "Writers", img: IMG.hist2 }, { title: "Technicians", img: IMG.archive },
    ],
  },
  "interviews": {
    title: "Interviews", accent: C.teal,
    subtitle: "Conversations with the people who shape Malayalam cinema.",
    items: [
      { title: "Latest Interview", img: IMG.intv1 }, { title: "Video Interviews", img: IMG.intv2 },
      { title: "Audio Interviews", img: IMG.news }, { title: "Archive Interviews", img: IMG.hist1 },
    ],
  },
  "cinema-history": {
    title: "Cinema History", accent: C.gold,
    subtitle: "The story of Malayalam cinema — from the early talkies to the OTT era.",
    items: [
      { title: "Timeline", img: IMG.hist1 }, { title: "Landmark Films", img: IMG.hist2 },
      { title: "Studios", img: IMG.archive }, { title: "Music History", img: IMG.star },
      { title: "Television Era", img: IMG.news }, { title: "OTT Era", img: IMG.kerala2 },
    ],
  },
  "memories": {
    title: "Memories", accent: C.olive,
    subtitle: "Tributes and remembrances of the artists we have lost.",
    items: [
      { title: "In Memoriam", img: IMG.mem1 }, { title: "Tributes", img: IMG.mem2 },
      { title: "Rare Photographs", img: IMG.hist2 }, { title: "Legacy", img: IMG.archive },
    ],
  },
  "gallery": {
    title: "Galleries", accent: C.rust,
    subtitle: "Photographs, events, posters and captioned collections.",
    items: [
      { title: "Events", img: IMG.kerala1 }, { title: "Posters", img: IMG.nowShowing },
      { title: "Behind the Scenes", img: IMG.intv2 }, { title: "Portraits", img: IMG.star },
      { title: "Premieres", img: IMG.kerala3 }, { title: "Archive Albums", img: IMG.hist1 },
    ],
  },
  "about-amma": {
    title: "About AMMA", accent: C.navy,
    subtitle: "Association of Malayalam Movie Artists — historical archive and information.",
    items: [
      { title: "History", img: IMG.hist1 }, { title: "Milestones", img: IMG.archive },
      { title: "Events Archive", img: IMG.mem1 },
    ],
  },
  "star-address": {
    title: "Star Address", accent: C.teal,
    subtitle: "Professional and representation contacts for the industry.",
    items: [
      { title: "Representation", img: IMG.intv1 }, { title: "Production Houses", img: IMG.news },
      { title: "Guilds & Unions", img: IMG.archive },
    ],
  },
};

const Card = ({ item, accent, testid }) => (
  <a href="#" data-testid={testid}
    className="group flex flex-col overflow-hidden rounded-xl border transition-all hover:-translate-y-1 hover:shadow-lg"
    style={{ background: C.card, borderColor: C.line }}>
    <div className="overflow-hidden">
      <img src={item.img} alt={item.title} className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
    </div>
    <div className="flex items-center justify-between px-4 py-3">
      <span className="font-display text-lg font-semibold" style={{ color: C.navy }}>{item.title}</span>
      <span className="flex h-7 w-7 items-center justify-center rounded-full" style={{ background: accent }}>
        <ChevronRight className="h-4 w-4 text-white" />
      </span>
    </div>
  </a>
);

const PostCard = ({ post, section, accent, lang, fallbackImg, testid }) => {
  const title = (lang === "ml" ? post.title_ml : post.title_en) || post.title_en || post.title_ml || "(untitled)";
  const img = post.cover_image ? fileUrl(post.cover_image) : fallbackImg;
  return (
    <Link to={`/malayalam/${section}/${post.slug}`} data-testid={testid}
      className={`group flex flex-col overflow-hidden rounded-xl border transition-all hover:-translate-y-1 hover:shadow-lg ${lang === "ml" ? "ml-lang-ml" : ""}`}
      style={{ background: C.card, borderColor: C.line }}>
      <div className="overflow-hidden">
        <img src={img} alt={title} className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <span className="font-display text-lg font-semibold" style={{ color: C.navy }}>{title}</span>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full" style={{ background: accent }}>
            <ChevronRight className="h-4 w-4 text-white" />
          </span>
        </div>
        {post.date && <span className="text-xs" style={{ color: C.muted }}>{post.date}</span>}
      </div>
    </Link>
  );
};

export default function MalayalamSection() {
  const { section } = useParams();
  const { lang, t } = useMalayalamLang();
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const cfg = SECTIONS[section] || {
    title: "Section", accent: C.gold, subtitle: "Content from the archive will appear here as it is migrated and reviewed.", items: [],
  };

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    mlPublicList(section).then((d) => { if (alive) { setPosts(d.items || []); setLoaded(true); } })
      .catch(() => { if (alive) { setPosts([]); setLoaded(true); } });
    return () => { alive = false; };
  }, [section]);

  const hasPosts = posts.length > 0;
  const fallbacks = [IMG.nowShowing, IMG.kerala1, IMG.star, IMG.archive, IMG.hist1, IMG.intv1];

  return (
    <MalayalamLayout>
      <section data-testid={`ml-section-${section}`}>
        {/* Section header band */}
        <div className="border-b" style={{ borderColor: C.line, background: "linear-gradient(180deg, #F3E9D2 0%, #FBF6EC 100%)" }}>
          <div className="mx-auto max-w-[1500px] px-6 py-10">
            <div className="mb-2 h-1 w-16 rounded-full" style={{ background: cfg.accent }} />
            <h1 className="font-display text-4xl font-bold sm:text-5xl" style={{ color: C.navy }} data-testid="ml-section-title">{t(cfg.title)}</h1>
            <p className="mt-3 max-w-2xl text-base" style={{ color: C.muted }}>{t(cfg.subtitle)}</p>
          </div>
        </div>

        {/* Grid */}
        <div className="mx-auto max-w-[1500px] px-6 py-10">
          {hasPosts ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((p, i) => (
                <PostCard key={p.id} post={p} section={section} accent={cfg.accent} lang={lang} fallbackImg={fallbacks[i % fallbacks.length]} testid={`ml-post-${p.id}`} />
              ))}
            </div>
          ) : (
            <>
              {cfg.items.length > 0 && (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {cfg.items.map((item, i) => (
                    <Card key={i} item={item} accent={cfg.accent} testid={`ml-item-${section}-${i}`} />
                  ))}
                </div>
              )}
              {loaded && (
                <p className="mt-10 rounded-lg border border-dashed px-5 py-4 text-center text-sm" style={{ borderColor: C.line, color: C.muted }} data-testid="ml-section-note">
                  {t("Content from the archive will appear here as it is migrated and reviewed.")}
                </p>
              )}
            </>
          )}
        </div>
      </section>
    </MalayalamLayout>
  );
}
